<template>
  <div class="step-wrapper">
    <template v-if="isQuestion">
      <div class="step-main">
        <div class="step-main--text">
          <span v-html="stepData.question.text" />
          <GekkoButton
            v-if="stepData.question.extra_info"
            icon="fa fa-question-circle"
            variant="link"
            class="text-blue p-0"
            v-b-tooltip.click="stepData.question.extra_info"
          />
        </div>
        <template v-if="stepData.question.type === 'choose-one'">
          <div class="survey-options-wrap">
            <div
              v-for="(option, index) in stepData.question.options"
              :key="'step-op-' + index"
              class="survey-option-box"
            >
              <div class="custom-control custom-radio">
                <input
                  type="radio"
                  :id="'step-op-' + index"
                  :name="'step-ops'"
                  :value="option.value"
                  :checked="answers[stepData.id] && answers[stepData.id].value === option.value"
                  class="custom-control-input"
                  @change="selectOption(option)"
                />
                <label :for="'step-op-' + index" class="custom-control-label">{{ option.text }}</label>
              </div>
            </div>
          </div>
        </template>

        <template v-if="stepData.question.type === 'dialog'">
          <div class="dialog-buttons mt-4">
            <div v-for="(option, index) in stepData.question.options" :key="'step-op-' + index">
              <GekkoButton :text="option.text" :variant="option.variant || 'primary'" @click="selectOption(option, true)" />
            </div>
          </div>
        </template>
      </div>
    </template>

    <template v-if="isSubcomponent">
      <div class="step-substep">
        <component
          :is="stepComponent"
          v-on="$listeners"
          @save-answer="saveAnswer"
          :component-data="stepData.component.data"
        />
      </div>
    </template>

    <template v-if="isMessage">
      <div class="question-text">
        {{ stepData.question.text }}
      </div>
      <div style="display: flex; justify-content: flex-end">
        <GekkoButton
          v-if="stepData.nextbutton"
          :text="stepData.nextbutton"
          @click="nextStep"
          icon="fa-solid fa-arrow-right"
          icon-right
        />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import GekkoButton from '@/components/GekkoButton'

const surveyComponents = {
  GoodsImported: () => import('@/components/ui/survey/GoodsImported.vue'),
  GiftsExceed: () => import('@/components/ui/survey/GiftsExceed.vue'),
  PrivateCarCosts: () => import('@/components/ui/survey/PrivateCarCosts.vue'),
  PrivateExpenses: () => import('@/components/ui/survey/PrivateExpenses.vue'),
}

export default {
  name: 'SurveyStep',
  components: {
    GekkoButton,
  },
  props: {
    stepData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {}
  },
  computed: {
    isQuestion() {
      return this.stepData.type === 'question'
    },
    isMessage() {
      return this.stepData.type === 'message'
    },
    isSubcomponent() {
      return this.stepData.type === 'subcomponent'
    },
    subcomponentName() {
      return this.isSubcomponent ? this.stepData.component?.name : null
    },
    stepComponent() {
      return this.subcomponentName ? surveyComponents[this.subcomponentName] || null : null
    },
    ...mapGetters('survey', {
      answers: 'getAnswers',
    }),
  },
  methods: {
    selectOption(option, emit = false) {
      this.$emit('save-answer', { id: this.stepData.id, value: option.value, text: this.stepData.subtitle })
      if (emit) {
        if (option.fn.name == 'nextStep') {
          this.$emit('next-step', false)
        }
        if (option.fn.name == 'close') {
          this.$emit('close', false)
        }
      }
    },
    normalizeAnswerValue(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }

      if (typeof value === 'number') {
        return Number.isFinite(value) ? Math.round(value) : 0
      }

      if (typeof value === 'string') {
        const normalizedValue = value.replace(',', '.').trim()

        if (normalizedValue === '') {
          return 0
        }

        const parsedValue = Number(normalizedValue)
        return Number.isFinite(parsedValue) ? Math.round(parsedValue) : value
      }

      return value
    },
    saveAnswer(option) {
      Object.values(option.answers).forEach((answer) => {
        this.$emit('save-answer', {
          ...answer,
          value: this.normalizeAnswerValue(answer.value),
        })
      })
    },
  },
}
</script>
<style lang ="scss">
.survey-substep-wrap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
<style lang="scss" scoped>
.step-wrapper {
  font-size: 16px;
  .step-main {
    &--text {
      padding-bottom: 1rem;
    }
  }
  .survey-options-wrap {
    display: flex;
    gap: 0.5rem;
    flex-direction: row;
    justify-content: center;
    padding: 1.5rem;
    .survey-option-box {
      width: 125px;

      .custom-control {
        padding: 0;

        .custom-control-input:checked ~ .custom-control-label {
          background-color: rgba(24, 119, 242, 0.08);
          border-color: $colorUnassigned;
        }

        .custom-control-label {
          width: 100%;
          cursor: pointer;

          padding: 1rem;
          padding-left: 2.5rem;
          border: 1px solid $colorBorderDash;
          border-radius: $radiusMd;
          &:before, &:after {
            top: 1.25rem;
            left: 0.75rem;
          }
          &:hover {
            border: 1px solid $colorUnassigned;
          }
        }
      }
    }
  }
}

.dialog-buttons {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}
</style>
